import { useState, useEffect, useCallback } from 'react';

export interface RecentLocationsStorage {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

const STORAGE_KEY = 'recentLocations';

export function useRecentLocations(
  storage: RecentLocationsStorage,
  maxItems: number = 5,
) {
  const [recentLocations, setRecentLocations] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve(storage.getItem(STORAGE_KEY))
      .then((saved) => {
        if (cancelled || !saved) return;
        try {
          setRecentLocations(JSON.parse(saved));
        } catch (error) {
          console.error('Error parsing recent locations:', error);
          void storage.removeItem(STORAGE_KEY);
        }
      })
      .catch((error) => console.error('Error loading recent locations:', error));
    return () => {
      cancelled = true;
    };
  }, [storage]);

  useEffect(() => {
    if (recentLocations.length > 0) {
      void storage.setItem(STORAGE_KEY, JSON.stringify(recentLocations));
    }
  }, [recentLocations, storage]);

  const addRecentLocation = useCallback(
    (location: string) => {
      if (!location) return;
      setRecentLocations((prev) => {
        const filtered = prev.filter((loc) => loc !== location);
        return [location, ...filtered].slice(0, maxItems);
      });
    },
    [maxItems],
  );

  const clearRecentLocations = useCallback(() => {
    void storage.removeItem(STORAGE_KEY);
    setRecentLocations([]);
  }, [storage]);

  return { recentLocations, addRecentLocation, clearRecentLocations };
}
